import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getOptionalLeoUuid,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { QuestionType } from "../types/questionType";
import { QuestionDetails, QuestionDetailsDTO } from "../types/questionDetails";
import { SurveyDetailsAndQuestions, SurveyDetailsAndQuestionsDTO } from "../types/surveyDetailsAndQuestions";
import { Code, CodeDTO } from "../types/code";
/* eslint-disable import/export */
export abstract class AddSurveyQuestionRPC implements
  LeoRPC<AddSurveyQuestionRPC.Request, AddSurveyQuestionRPC.Response, AddSurveyQuestionRPC.Errors.Errors>
{
  abstract execute(
    request: AddSurveyQuestionRPC.Request
  ): Promise<LeoRPCResult<AddSurveyQuestionRPC.Response, AddSurveyQuestionRPC.Errors.Errors>>;
}

export namespace AddSurveyQuestionRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    questionType: QuestionType.QuestionTypeDTO;
    questionDetails: QuestionDetailsDTO;
    projectId: string;
    parentQuestionId?: string | null;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isAddSurveyQuestionRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly questionType: QuestionType.QuestionType;
    readonly questionDetails: QuestionDetails;
    readonly projectId: LeoUUID;
    readonly parentQuestionId: LeoUUID | null;
  
  
    constructor(
    surveyId: LeoUUID, 
    questionType: QuestionType.QuestionType, 
    questionDetails: QuestionDetails, 
    projectId: LeoUUID, 
    parentQuestionId: LeoUUID | null = null
    ) {
      this.surveyId = surveyId;
      this.questionType = questionType;
      this.questionDetails = questionDetails;
      this.projectId = projectId;
      this.parentQuestionId = parentQuestionId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const questionType = QuestionType.fromDTO(getObject(dto, "questionType", InvalidRequestQuestionTypeError));
      const questionDetails = QuestionDetails.fromDTO(getObject(dto, "questionDetails", InvalidRequestQuestionDetailsError));
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const parentQuestionId = getOptionalLeoUuid(dto, "parentQuestionId", InvalidRequestParentQuestionIdError);
      return new Request(
        surveyId,
        questionType,
        questionDetails,
        projectId,
        parentQuestionId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          questionType: QuestionType.toDTO(this.questionType),
          questionDetails: this.questionDetails.toDTO(),
          projectId: this.projectId.toDTO(),
      };
      if (this.parentQuestionId !== undefined && this.parentQuestionId !== null){
        dto.parentQuestionId = this.parentQuestionId.toDTO();
      }
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `AddSurveyQuestionRPC(surveyId='${this.surveyId}',questionType='${this.questionType}',questionDetails='${this.questionDetails}',projectId='${this.projectId}',parentQuestionId='${this.parentQuestionId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    surveyDetailsAndQuestions: SurveyDetailsAndQuestionsDTO;
    questionId: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isAddSurveyQuestionResponse: boolean = true;
  
    readonly surveyDetailsAndQuestions: SurveyDetailsAndQuestions;
    readonly questionId: LeoUUID;
  
  
    constructor(
    surveyDetailsAndQuestions: SurveyDetailsAndQuestions, 
    questionId: LeoUUID
    ) {
      this.surveyDetailsAndQuestions = surveyDetailsAndQuestions;
      this.questionId = questionId;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyDetailsAndQuestions = SurveyDetailsAndQuestions.fromDTO(getObject(dto, "surveyDetailsAndQuestions", InvalidResponseSurveyDetailsAndQuestionsError));
      const questionId = getLeoUuid(dto, "questionId", InvalidResponseQuestionIdError);
      return new Response(
        surveyDetailsAndQuestions,
        questionId
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyDetailsAndQuestions: this.surveyDetailsAndQuestions.toDTO(),
          questionId: this.questionId.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `AddSurveyQuestion(surveyDetailsAndQuestions='${this.surveyDetailsAndQuestions}',questionId='${this.questionId}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    MaxQuestionsReached = "MAX_QUESTIONS_REACHED",
    QuestionCodeNotUnique = "QUESTION_CODE_NOT_UNIQUE",
    OptionCodeNotUnique = "OPTION_CODE_NOT_UNIQUE",
    JsonVersionNotSupported = "JSON_VERSION_NOT_SUPPORTED",
    InvalidQuestionDetailsJson = "INVALID_QUESTION_DETAILS_JSON",
    InvalidParentQuestionId = "INVALID_PARENT_QUESTION_ID",
    InvalidChildQuestionType = "INVALID_CHILD_QUESTION_TYPE",
    InvalidMaxSelection = "INVALID_MAX_SELECTION"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `AddSurveyQuestionRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `AddSurveyQuestionRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
      }
    
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `AddSurveyQuestionRPC.SURVEY_IS_CLOSED()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `AddSurveyQuestionRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface MaxQuestionsReachedDTO {
      code: string;
    }
    
    export class MaxQuestionsReached extends LeoRPCError<RPCError> {
      readonly isMaxQuestionsReached: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.MaxQuestionsReached;
        super(code);
      }
    
    
      toDTO(): MaxQuestionsReachedDTO {
        const dto: MaxQuestionsReachedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    MaxQuestionsReached.prototype.toString = function toString(): string {
      return `AddSurveyQuestionRPC.MAX_QUESTIONS_REACHED()`;
    };
    
    
    export interface QuestionCodeNotUniqueDTO {
      code: string;
    }
    
    export class QuestionCodeNotUnique extends LeoRPCError<RPCError> {
      readonly isQuestionCodeNotUnique: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.QuestionCodeNotUnique;
        super(code);
      }
    
    
      toDTO(): QuestionCodeNotUniqueDTO {
        const dto: QuestionCodeNotUniqueDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    QuestionCodeNotUnique.prototype.toString = function toString(): string {
      return `AddSurveyQuestionRPC.QUESTION_CODE_NOT_UNIQUE()`;
    };
    
    
    export interface OptionCodeNotUniqueDTO {
      code: string;
      optionCode: CodeDTO;
    }
    
    export class OptionCodeNotUnique extends LeoRPCError<RPCError> {
      readonly isOptionCodeNotUnique: boolean = true;
    
      readonly optionCode: Code;
    
      constructor(
      optionCode: Code
      ) {
        const code = RPCError.OptionCodeNotUnique;
        super(code);
        this.optionCode = optionCode;
      }
    
      static fromDTO(dto: OptionCodeNotUniqueDTO): OptionCodeNotUnique {
        const optionCode = Code.fromDTO(getObject(dto, "optionCode", InvalidResponseOptionCodeNotUniqueOptionCodeError));
        return new OptionCodeNotUnique(
          optionCode
        );
      }
    
      toDTO(): OptionCodeNotUniqueDTO {
        const dto: OptionCodeNotUniqueDTO = {
          code: this.code,
          optionCode: this.optionCode.toDTO()
        };
        return dto;
      }
    
      }
    
    OptionCodeNotUnique.prototype.toString = function toString(): string {
      return `AddSurveyQuestionRPC.OPTION_CODE_NOT_UNIQUE(optionCode='${this.optionCode}')`;
    };
    
    
    export interface JsonVersionNotSupportedDTO {
      code: string;
    }
    
    export class JsonVersionNotSupported extends LeoRPCError<RPCError> {
      readonly isJsonVersionNotSupported: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.JsonVersionNotSupported;
        super(code);
      }
    
    
      toDTO(): JsonVersionNotSupportedDTO {
        const dto: JsonVersionNotSupportedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    JsonVersionNotSupported.prototype.toString = function toString(): string {
      return `AddSurveyQuestionRPC.JSON_VERSION_NOT_SUPPORTED()`;
    };
    
    
    export interface InvalidQuestionDetailsJsonDTO {
      code: string;
    }
    
    export class InvalidQuestionDetailsJson extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionDetailsJson: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionDetailsJson;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionDetailsJsonDTO {
        const dto: InvalidQuestionDetailsJsonDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionDetailsJson.prototype.toString = function toString(): string {
      return `AddSurveyQuestionRPC.INVALID_QUESTION_DETAILS_JSON()`;
    };
    
    
    export interface InvalidParentQuestionIdDTO {
      code: string;
    }
    
    export class InvalidParentQuestionId extends LeoRPCError<RPCError> {
      readonly isInvalidParentQuestionId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidParentQuestionId;
        super(code);
      }
    
    
      toDTO(): InvalidParentQuestionIdDTO {
        const dto: InvalidParentQuestionIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidParentQuestionId.prototype.toString = function toString(): string {
      return `AddSurveyQuestionRPC.INVALID_PARENT_QUESTION_ID()`;
    };
    
    
    export interface InvalidChildQuestionTypeDTO {
      code: string;
    }
    
    export class InvalidChildQuestionType extends LeoRPCError<RPCError> {
      readonly isInvalidChildQuestionType: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidChildQuestionType;
        super(code);
      }
    
    
      toDTO(): InvalidChildQuestionTypeDTO {
        const dto: InvalidChildQuestionTypeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidChildQuestionType.prototype.toString = function toString(): string {
      return `AddSurveyQuestionRPC.INVALID_CHILD_QUESTION_TYPE()`;
    };
    
    
    export interface InvalidMaxSelectionDTO {
      code: string;
    }
    
    export class InvalidMaxSelection extends LeoRPCError<RPCError> {
      readonly isInvalidMaxSelection: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidMaxSelection;
        super(code);
      }
    
    
      toDTO(): InvalidMaxSelectionDTO {
        const dto: InvalidMaxSelectionDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidMaxSelection.prototype.toString = function toString(): string {
      return `AddSurveyQuestionRPC.INVALID_MAX_SELECTION()`;
    };
    

    export type Errors = InvalidSurveyId | InvalidProjectId | SurveyIsClosed | ProjectAlreadyArchived | MaxQuestionsReached | QuestionCodeNotUnique | OptionCodeNotUnique | JsonVersionNotSupported | InvalidQuestionDetailsJson | InvalidParentQuestionId | InvalidChildQuestionType | InvalidMaxSelection;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidProjectIdDTO | SurveyIsClosedDTO | ProjectAlreadyArchivedDTO | MaxQuestionsReachedDTO | QuestionCodeNotUniqueDTO | OptionCodeNotUniqueDTO | JsonVersionNotSupportedDTO | InvalidQuestionDetailsJsonDTO | InvalidParentQuestionIdDTO | InvalidChildQuestionTypeDTO | InvalidMaxSelectionDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.SurveyIsClosed:
          return new SurveyIsClosed();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.MaxQuestionsReached:
          return new MaxQuestionsReached();
        case RPCError.QuestionCodeNotUnique:
          return new QuestionCodeNotUnique();
        case RPCError.OptionCodeNotUnique:
          return OptionCodeNotUnique.fromDTO(dto as OptionCodeNotUniqueDTO);
        case RPCError.JsonVersionNotSupported:
          return new JsonVersionNotSupported();
        case RPCError.InvalidQuestionDetailsJson:
          return new InvalidQuestionDetailsJson();
        case RPCError.InvalidParentQuestionId:
          return new InvalidParentQuestionId();
        case RPCError.InvalidChildQuestionType:
          return new InvalidChildQuestionType();
        case RPCError.InvalidMaxSelection:
          return new InvalidMaxSelection();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends AddSurveyQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestQuestionTypeError extends AddSurveyQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuestionTypeError";
  }
}

export class InvalidRequestQuestionDetailsError extends AddSurveyQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuestionDetailsError";
  }
}

export class InvalidRequestProjectIdError extends AddSurveyQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestParentQuestionIdError extends AddSurveyQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestParentQuestionIdError";
  }
}

export class InvalidResponseSurveyDetailsAndQuestionsError extends AddSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyDetailsAndQuestionsError";
  }
}

export class InvalidResponseQuestionIdError extends AddSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestionIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends AddSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends AddSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseSurveyIsClosedError extends AddSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends AddSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseMaxQuestionsReachedError extends AddSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMaxQuestionsReachedError";
  }
}

export class InvalidResponseQuestionCodeNotUniqueError extends AddSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestionCodeNotUniqueError";
  }
}

export class InvalidResponseOptionCodeNotUniqueError extends AddSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseOptionCodeNotUniqueError";
  }
}

export class InvalidResponseOptionCodeNotUniqueOptionCodeError extends InvalidResponseOptionCodeNotUniqueError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseOptionCodeNotUniqueOptionCodeError";
  }
}
export class InvalidResponseJsonVersionNotSupportedError extends AddSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseJsonVersionNotSupportedError";
  }
}

export class InvalidResponseInvalidQuestionDetailsJsonError extends AddSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionDetailsJsonError";
  }
}

export class InvalidResponseInvalidParentQuestionIdError extends AddSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidParentQuestionIdError";
  }
}

export class InvalidResponseInvalidChildQuestionTypeError extends AddSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidChildQuestionTypeError";
  }
}

export class InvalidResponseInvalidMaxSelectionError extends AddSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidMaxSelectionError";
  }
}

